import {
  Blocks,
  Building2,
  Home,
  MessageSquare,
  ShoppingCart,
  Package,
  Settings,
  Settings2,
  Users,
  Boxes,
  GitBranch,
  Archive,
  BarChart3,
  Receipt,
  DollarSign,
  Brain,
  ShipWheel,
  Gauge,
  type LucideIcon,
} from "lucide-react";

export interface PageConfig {
  icon: LucideIcon;
  iconBgColor: string;
  iconColor: string;
  titleKey: string;
  subtitleKey?: string;
}

/**
 * Page configuration for all routes
 * Defines icon, colors, title and optional subtitle for each page
 */
export const pageConfigs: Record<string, PageConfig> = {
  "/home": {
    icon: Home,
    iconBgColor: "bg-green-100",
    iconColor: "text-green-600",
    titleKey: "navigation.home",
  },
  "/monitoring": {
    icon: Gauge,
    iconBgColor: "bg-emerald-100",
    iconColor: "text-emerald-600",
    titleKey: "navigation.monitoring",
  },
  "/purchases": {
    icon: Blocks,
    iconBgColor: "bg-purple-100",
    iconColor: "text-purple-600",
    titleKey: "navigation.purchases",
    subtitleKey: "purchases.subtitle",
  },
  "/recap": {
    icon: ShoppingCart,
    iconBgColor: "bg-green-100",
    iconColor: "text-green-600",
    titleKey: "navigation.recap",
    subtitleKey: "recap.subtitle",
  },
  "/chat": {
    icon: MessageSquare,
    iconBgColor: "bg-blue-100",
    iconColor: "text-blue-600",
    titleKey: "navigation.messages",
  },
  "/products": {
    icon: Package,
    iconBgColor: "bg-orange-100",
    iconColor: "text-orange-600",
    titleKey: "navigation.products",
  },
  "/stocks": {
    icon: Boxes,
    iconBgColor: "bg-amber-100",
    iconColor: "text-amber-600",
    titleKey: "navigation.stocks",
  },
  "/lots": {
    icon: Archive,
    iconBgColor: "bg-yellow-100",
    iconColor: "text-yellow-600",
    titleKey: "navigation.lots",
  },
  "/flows": {
    icon: GitBranch,
    iconBgColor: "bg-violet-100",
    iconColor: "text-violet-600",
    titleKey: "navigation.flows",
  },
  "/reports": {
    icon: BarChart3,
    iconBgColor: "bg-sky-100",
    iconColor: "text-sky-600",
    titleKey: "navigation.reports",
  },
  "/operations": {
    icon: Receipt,
    iconBgColor: "bg-indigo-100",
    iconColor: "text-indigo-600",
    titleKey: "navigation.operations",
    subtitleKey: "operations.subtitle",
  },
  "/partners": {
    icon: Users,
    iconBgColor: "bg-teal-100",
    iconColor: "text-teal-600",
    titleKey: "navigation.partners",
  },
  "/pricing": {
    icon: DollarSign,
    iconBgColor: "bg-green-100",
    iconColor: "text-green-600",
    titleKey: "navigation.pricing",
  },
  "/parameters": {
    icon: Settings,
    iconBgColor: "bg-gray-100",
    iconColor: "text-gray-600",
    titleKey: "navigation.parameters",
  },
  "/company": {
    icon: Building2,
    iconBgColor: "bg-green-100",
    iconColor: "text-green-600",
    // The header overrides this with the live company name; this is the
    // pre-load fallback.
    titleKey: "company.title",
    subtitleKey: "company.subtitle",
  },
  "/steering": {
    icon: ShipWheel,
    iconBgColor: "bg-emerald-100",
    iconColor: "text-emerald-600",
    titleKey: "navigation.businessIntelligence",
  },
  "/contacts": {
    icon: Users,
    iconBgColor: "bg-cyan-100",
    iconColor: "text-cyan-600",
    titleKey: "navigation.contacts",
  },
  "/saved-offers": {
    icon: Archive,
    iconBgColor: "bg-lime-100",
    iconColor: "text-lime-600",
    titleKey: "navigation.savedOffers",
  },
  "/ai": {
    icon: Brain,
    iconBgColor: "bg-violet-100",
    iconColor: "text-violet-600",
    titleKey: "common.aiManagement.title",
  },
  "/preferences": {
    icon: Settings2,
    iconBgColor: "bg-gray-100",
    iconColor: "text-gray-600",
    titleKey: "preferences.title",
  },
};

/**
 * Get page config for a given pathname
 * Falls back to a default config if no match found
 */
export function getPageConfig(pathname: string): PageConfig | null {
  // Direct match
  if (pageConfigs[pathname]) {
    return pageConfigs[pathname];
  }

  // Try to match the base path (for routes like /products/[id])
  const basePath = "/" + pathname.split("/")[1];
  if (pageConfigs[basePath]) {
    return pageConfigs[basePath];
  }

  return null;
}
